import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import styles from '../Row.module.scss';

import Button from '../../design-system/button/Button';
import { convertStringsOfDateAndTimeToDate, convertToLocaleAndFormat } from '../../../utils/time-helpers';
import Dropdown from '../../design-system/dropdown/Dropdown';
import { registerTutorAttendance, cleanChangeAppointmentStatus } from '../../../actions/appointments';
import {
  getPastAppointmentStatusLabelFromValue,
  isStatusValid,
  parseHappenedToAttended,
  pastAppointmentStatus,
  tutorAttendanceStatus
} from '../../../domains/appointments/status';
import { add, isAfter, isWithinInterval, sub } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import SuccessToast from '../../design-system/toast/successToast/SuccessToast';
import ErrorToast from '../../design-system/toast/errorToast/ErrorToast';
import { videoPlatformsKeys } from '../../../domains/user/video-platforms';
import { capitalize } from '../../../utils/string-helpers';
import JoinTeams from '../../informative-modals/JoinTeams/join-teams';
import ConfirmStatusChange from '../ConfirmStatusChange';
import { changeAppointmentStatus } from '../../../actions/appointments';

const Row = ({
  appointment,
  changeAppointmentStatusAction,
  cleanChangeAppointmentStatusAction,
  errorChangingAppointmentStatus,
  isChangingAppointmentStatus,
  isChangingAppointmentStatusSuccess,
  registerTutorAttendanceAction,
  user
}) => {
  const [status, setStatus] = useState(appointment?.status);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showTeamsModal, setShowTeamsModal] = useState(false);
  const [teamsEmail, setTeamsEmail] = useState('');

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const getApptDate = () => {
    const apptDate = getApptFullDate();
    return <p>{convertToLocaleAndFormat(apptDate, timezone, 'yyyy-MM-dd')}</p>;
  };

  const getApptFullDate = () => {
    return convertStringsOfDateAndTimeToDate(appointment?.date, appointment?.start_time);
  };

  const getTimezoned = () => {
    const apptDate = getApptFullDate();
    return convertToLocaleAndFormat(apptDate, timezone, 'HH:mm');
  };

  const getApptDuration = () => {
    return <p>{appointment?.duration} min</p>;
  };

  const getStudentPreferredPlatform = () => {
    if (!appointment?.students?.length) return '';
    return appointment.students?.[0]?.preferred_class;
  };

  const getPreferredPlatform = () => {
    const preferredPlatform = getStudentPreferredPlatform(appointment);
    return {
      preferredPlatform,
      link: user[videoPlatformsKeys[preferredPlatform]]
    };
  };

  const getJoinButton = () => {
    const timezoned = utcToZonedTime(getApptFullDate(), timezone);
    const { preferredPlatform } = getPreferredPlatform();

    if (
      !isWithinInterval(new Date(), {
        start: sub(timezoned, { minutes: 15 }),
        end: add(timezoned, { minutes: appointment.duration })
      })
    )
      return <p className={styles['status-blocked']}>{capitalize(preferredPlatform)}</p>;

    return (
      <div className={styles['join-wrapper']}>
        <Button outfit="greenButton" onClick={handleJoin}>
          Join {capitalize(preferredPlatform)}
        </Button>
      </div>
    );
  };

  const getTutorAttendance = () => {
    if (tutorAttendanceStatus[appointment?.tutor_attendance]) return tutorAttendanceStatus[appointment?.tutor_attendance];
    if (appointment?.tutor_attendance) return appointment?.tutor_attendance;
    return '';
  };

  const getStudentFullName = () => {
    const fullName = `${appointment?.students?.[0]?.first_name} ${appointment?.students?.[0]?.last_name}`;

    if (appointment?.is_alternative_coach) {
      return (
        <div>
          <div>{fullName}</div>
          <div className={styles['one-off-learner']}>Non-regular learner</div>
        </div>
      );
    }
    return fullName;
  };

  const getStudentOutcome = () => {
    const timezonedAppt = utcToZonedTime(getApptFullDate(), timezone);
    const endOfAppointment = add(timezonedAppt, { minutes: appointment.duration });
    const timezonedNow = new Date();
    if (isAfter(endOfAppointment, timezonedNow)) return null;

    const parsedStatus = parseHappenedToAttended(status || appointment?.status);

    if (isStatusValid(parsedStatus)) return <p>{getPastAppointmentStatusLabelFromValue(parsedStatus)}</p>;

    return (
      <Dropdown
        key={`${appointment?.id}-${parsedStatus}`}
        defaultValue={parsedStatus}
        onSelect={handleSelection}
        options={[
          { label: pastAppointmentStatus.studentAttended.label, value: pastAppointmentStatus.studentAttended.value },
          { label: pastAppointmentStatus.studentNoShow.label, value: pastAppointmentStatus.studentNoShow.value },
          { label: pastAppointmentStatus.technicalIssues.label, value: pastAppointmentStatus.technicalIssues.value }
        ]}
      />
    );
  };

  const handleJoin = () => {
    const { preferredPlatform, link } = getPreferredPlatform();

    if (preferredPlatform === 'teams') {
      setTeamsEmail(link);
      setShowTeamsModal(true);
    } else {
      window.open(link, '_blank', 'noopener,noreferrer');
    }

    registerTutorAttendanceAction({ token: user?.token, appointmentId: appointment?.id });
  };

  const changeAppointmentStatus = (message) => {
    changeAppointmentStatusAction({
      token: user?.token,
      appointmentId: appointment?.id,
      status,
      message,
      successCallback: () => setShowConfirmationModal(false)
    });
  };

  const handleSelection = (st) => {
    setStatus(st);
    setShowConfirmationModal(true);
  };

  const onCloseConfirmationModal = () => {
    setStatus('');
    setShowConfirmationModal(false);
  };

  return (
    <tr className={styles.row}>
      <td>
        <p>{getApptDate()}</p>
      </td>
      <td>
        <p>{getTimezoned()}</p>
      </td>
      <td>
        <p>{getApptDuration()}</p>
      </td>
      <td>
        <p>{getJoinButton()}</p>
      </td>
      <td>{getStudentFullName()}</td>
      <td>
        <p>{getTutorAttendance()}</p>
      </td>
      <td>{getStudentOutcome()}</td>
      {showTeamsModal && <JoinTeams onClose={() => setShowTeamsModal(false)} teamsEmail={teamsEmail} />}
      {showConfirmationModal && (
        <ConfirmStatusChange
          onConfirm={changeAppointmentStatus}
          onClose={onCloseConfirmationModal}
          status={status}
          isLoading={isChangingAppointmentStatus}
        />
      )}
      <SuccessToast
        msg="Successfully updated your Live Practice status."
        show={isChangingAppointmentStatusSuccess}
        onClose={cleanChangeAppointmentStatusAction}
      />
      <ErrorToast
        errorMsg="Something went wrong while setting this class status. Please refresh the page and try again."
        show={errorChangingAppointmentStatus}
        onClose={cleanChangeAppointmentStatusAction}
      />
    </tr>
  );
};

Row.propTypes = {
  appointment: PropTypes.object.isRequired,
  changeAppointmentStatusAction: PropTypes.func.isRequired,
  cleanChangeAppointmentStatusAction: PropTypes.func.isRequired,
  errorChangingAppointmentStatus: PropTypes.bool,
  isChangingAppointmentStatus: PropTypes.bool,
  isChangingAppointmentStatusSuccess: PropTypes.bool,
  registerTutorAttendanceAction: PropTypes.func.isRequired,
  user: PropTypes.object
};

const mapStateToProps = (state) => ({
  errorChangingAppointmentStatus: state.appointments.errorChangingAppointmentStatus,
  isChangingAppointmentStatus: state.appointments.isChangingAppointmentStatus,
  isChangingAppointmentStatusSuccess: state.appointments.isChangingAppointmentStatusSuccess
});

export default connect(mapStateToProps, {
  changeAppointmentStatusAction: changeAppointmentStatus,
  cleanChangeAppointmentStatusAction: cleanChangeAppointmentStatus,
  registerTutorAttendanceAction: registerTutorAttendance
})(Row);
